import { useEffect, useCallback, useRef } from 'react'
import { useSellerQueueContext } from '../contexts/SellerQueueContext'
import * as sellerQueueService from '../services/sellerQueueService'
import { useSeller } from './useSeller'

export interface NoSaleFormData {
  reasonCode: number | null
  productName?: string
  sizeWanted?: string
  colorWanted?: string
  note?: string
}

/**
 * Hook: useSellerSession
 * Manages active seller service session (start, end, track duration)
 * Auto-refetches session if active
 */
export const useSellerSession = () => {
  const {
    currentSession,
    isLoadingSession,
    errorSession,
    setCurrentSession,
    setIsLoadingSession,
    setErrorSession,
    setLastUpdated,
  } = useSellerQueueContext()

  const { getTokenSeller } = useSeller()
  const sessionStartTimeRef = useRef<number | null>(null)

  const tokenSeller = getTokenSeller()
  const sellerId = tokenSeller?.id
  const pluginspaceId = tokenSeller?.pluginspaceId

  /**
   * Fetch current active session
   */
  const refetchSession = useCallback(async () => {
    if (!sellerId || !pluginspaceId) return

    try {
      setIsLoadingSession(true)
      setErrorSession(null)
      const session = await sellerQueueService.getCurrentSession(pluginspaceId, sellerId)
      setCurrentSession(session)
      setLastUpdated(new Date())
    } catch (error) {
      // No active session is normal
      setCurrentSession(null)
      setLastUpdated(new Date())
    } finally {
      setIsLoadingSession(false)
    }
  }, [sellerId, pluginspaceId, setCurrentSession, setIsLoadingSession, setErrorSession, setLastUpdated])

  /**
   * Start attending customer (backend will call seller)
   * Called when seller clicks "Iniciar Atendimento"
   */
  const startService = useCallback(async (_queueId: number) => {
    if (!sellerId) {
      throw new Error('Seller ID missing')
    }

    try {
      setIsLoadingSession(true)
      setErrorSession(null)

      // Use dedicated endpoint to create session and move seller to in_service atomically.
      await sellerQueueService.startSellerAttendance(sellerId)

      // Fetch fresh session data
      await refetchSession()
      setLastUpdated(new Date())
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      setErrorSession(err)
      throw err
    } finally {
      setIsLoadingSession(false)
    }
  }, [sellerId, refetchSession, setIsLoadingSession, setErrorSession, setLastUpdated])

  /**
   * End service session with outcome
   * Called when seller clicks "Resultado do Atendimento"
   */
  const endService = useCallback(
    async (outcome: string, noSaleData?: NoSaleFormData) => {
      if (!currentSession?.id) {
        throw new Error('No active session')
      }

      try {
        setIsLoadingSession(true)
        setErrorSession(null)

        // Calculate duration
        const durationSeconds = sessionStartTimeRef.current
          ? Math.floor((Date.now() - sessionStartTimeRef.current) / 1000)
          : 0

        // Complete session with outcome
        await sellerQueueService.completeSession(currentSession.id, outcome, durationSeconds)

        // If no_sale, create reason record
        if (outcome === 'no_sale' && noSaleData && noSaleData.reasonCode) {
          await sellerQueueService.createNoSaleReason(
            currentSession.id,
            noSaleData.reasonCode,
            noSaleData.productName,
            noSaleData.sizeWanted,
            noSaleData.colorWanted,
            noSaleData.note
          )
        }

        // Clear current session
        setCurrentSession(null)
        sessionStartTimeRef.current = null
        setLastUpdated(new Date())
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error))
        setErrorSession(err)
        throw err
      } finally {
        setIsLoadingSession(false)
      }
    },
    [currentSession, setCurrentSession, setIsLoadingSession, setErrorSession, setLastUpdated]
  )

  /**
   * Calculate elapsed time in current session
   */
  const getElapsedSeconds = useCallback(() => {
    const startedAtMs = currentSession?.startedAt
      ? new Date(currentSession.startedAt).getTime()
      : sessionStartTimeRef.current

    if (!startedAtMs) return 0
    return Math.floor((Date.now() - startedAtMs) / 1000)
  }, [currentSession?.startedAt])

  /**
   * Auto-refetch session every 5 seconds if active
   */
  useEffect(() => {
    if (!currentSession) return

    const interval = setInterval(refetchSession, 5000)
    return () => clearInterval(interval)
  }, [currentSession, refetchSession])

  /**
   * Set start time when session begins
   */
  useEffect(() => {
    if (currentSession?.status === 'in_progress' && !sessionStartTimeRef.current) {
      sessionStartTimeRef.current = currentSession.startedAt
        ? new Date(currentSession.startedAt).getTime()
        : Date.now()
    }
  }, [currentSession])

  return {
    currentSession,
    isLoading: isLoadingSession,
    error: errorSession,
    startService,
    endService,
    refetchSession,
    getElapsedSeconds,
  }
}

